import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { SearchBox } from 'components/search/SearchBox';
import { SearchFiltersTray } from 'components/organisms/SearchFiltersTray';
import { Button } from 'components/core/Button';
import { H1, Text, Paragraph } from 'components/core/Typography';
import { Filters } from 'components/svg';
import { compose, withProps, withState, withHandlers } from 'recompose';
import { withSearchHandler } from 'decorators';
import { locateUser } from 'utils/GeoLocation';
import { buildSearchLink } from 'lib/search';
import { setLocation } from 'lib/helpers';
import locationPinYellow from 'assets/images/icons/informed-search/location-pin-yellow.svg';

import './StickySearchBar.scss';

function NearMeSubheader({ className, geoError, queryParams, searchLocation, locateMe, locating }) {
	const hasCoords = queryParams.latitude && queryParams.longitude;
	const buttonLabel = geoError || !hasCoords ? 'Get current location' : 'Update Location';
	const statusMessage = geoError ? (
		<Text size="tiny" color="white" last>
			We could not determine your precise location.
			Please enable location in your browser settings.
		</Text>
	) : hasCoords ? (
		`Your location has now been set to ${searchLocation.text}.`
	) : (
		`It looks like you're in ${searchLocation.city}. Not correct?`
	);

	return (
		<Paragraph
			className={className}
			color="white"
			size="tiny"
			weight="ultraBold"
			last
		>
			{statusMessage}{' '}
			<button
				onClick={locateMe}
				disabled={locating}
				aria-label="Use my current location to find storage near me"
				className="near-me-btn"
			>
				<span className="location-img-container">
					<img
						src={locationPinYellow}
						alt="location"
						className="get-current-location__icon"
					/>
				</span>{' '}
				<span>{buttonLabel}</span>
			</button>
		</Paragraph>
	);
}

export function StickySearchBar({
	handleSearch,
	searchLocation,
	filtersShowing,
	handleToggleFilters,
	filtersCount,
	queryParams,
	onSearch,
	closeFilters,
	onSearchParamChange,
	filterComponent,
	isLarge,
	primaryHeadline,
	isNearMePage,
	locateMe,
	locating,
	geoError,
}) {
	const subheaderProps = { geoError, queryParams, searchLocation, locateMe, locating };

	return (
		<div className="sticky-search-bar-variation">
			{isNearMePage ? (
				<div className="near-me-header-container">
					<H1
						size="large"
						className="search-bar-label"
						color="white"
						family="rounded"
					>
						{primaryHeadline || `Storage in ${searchLocation.text}`}
					</H1>
					<NearMeSubheader className="near-me-desktop-subheader" {...subheaderProps} />
				</div>
			) : (
				<H1
					size="large"
					className="search-bar-label"
					color="white"
					family="rounded"
					last
				>
					{primaryHeadline || `Storage in ${searchLocation.text}`}
				</H1>
			)}
			<div className="search-bar-actions">
				<Button
					className={classnames('search-bar-filter-button', {
						open: filtersShowing,
					})}
					primary={false}
					type="button"
					onClick={handleToggleFilters}
				>
					<span
						className="search-bar-filter-icon"
						aria-hidden="true"
						color="yellow"
					>
						<Filters
							width={16}
							height={16}
						/>
					</span>
					Filters{' '}
					{filtersCount > 0 && (
						<Text
							size="tiny"
							last
							className="filter-count"
							aria-label={`${filtersCount} filters selected`}
						>
							{filtersCount}
						</Text>
					)}
				</Button>
				<SearchBox
					buttonFullWidth
					inputLabel={searchLocation.text}
					location={searchLocation.text}
					handleSearch={handleSearch}
					hideOnScroll
					buttonText="Find Storage"
				/>
			</div>
			{isNearMePage && (
				<NearMeSubheader className="near-me-mobile-subheader" {...subheaderProps} />
			)}
			{filtersShowing && isLarge && (
				<>
					<div className="search-filters-dropdown">
						<SearchFiltersTray
							handleSearch={handleSearch}
							handleToggleFilters={handleToggleFilters}
							queryParams={queryParams}
							onSearch={onSearch}
							closeFilters={closeFilters}
							filtersShowing={filtersShowing}
							onSearchParamChange={onSearchParamChange}
							isLarge={isLarge}
						>
							{filterComponent}
						</SearchFiltersTray>
					</div>
					<button
						type="button"
						className="search-filters-backdrop"
						onClick={closeFilters}
						aria-label="Close filters"
					/>
				</>
			)}
		</div>
	);
}

StickySearchBar.propTypes = {
	handleSearch: PropTypes.func.isRequired,
	searchLocation: PropTypes.object.isRequired,
	searchParams: PropTypes.object,
	searchPath: PropTypes.string,
	handleToggleFilters: PropTypes.func.isRequired,
	filtersShowing: PropTypes.bool.isRequired,
	isLarge: PropTypes.bool.isRequired,
	queryParams: PropTypes.object.isRequired,
	onSearch: PropTypes.func.isRequired,
	closeFilters: PropTypes.func.isRequired,
	onSearchParamChange: PropTypes.func.isRequired,
	filterComponent: PropTypes.node.isRequired,
	filtersCount: PropTypes.number.isRequired,
	primaryHeadline: PropTypes.string,
	isNearMePage: PropTypes.bool,
	locateMe: PropTypes.func,
	locating: PropTypes.bool,
	geoError: PropTypes.bool,
};

const enhance = compose(
	withProps(({ searchParams, searchPath }) => ({
		additionalSearchParams: {
			informedSearch: true,
		},
		searchPath: searchPath || '/search.html',
	})),
	withSearchHandler,
	withState('locating', 'setLocating', false),
	withState('geoError', 'setGeoError', false),
	withHandlers({
		locateMe: (props) => () => {
			if (props.locating) return;
			props.setLocating(true);
			props.setGeoError(false);

			locateUser({
				onSuccess: ({ latitude, longitude }) => {
					const params = {
						latitude,
						longitude,
						searchType: props.searchType,
						...(props.searchParams || {}),
					};
					setLocation(
						buildSearchLink(params, props.nearMeSearchPath),
					);
					props.setLocating(false);
				},
				onError: () => {
					props.setGeoError(true);
					props.setLocating(false);
				},
			});
		},
	}),
);

export default enhance(StickySearchBar);
